import React from 'react';
import styled from 'styled-components';
import { ChevronRight } from 'lucide-react';
import CustomerLogoUpload from './CustomerLogoUpload';

// ─── Shared bits ─────────────────────────────────────────────────────────────

function destinationsSummary(group, max) {
  if (!group?.destinations) return '';
  const arr = Array.from(group.destinations).filter(Boolean).sort((a, b) =>
    String(a).localeCompare(String(b))
  );
  if (arr.length === 0) return '';
  const head = arr.slice(0, max).join(' · ');
  const extra = arr.length - max;
  return extra > 0 ? `${head} · +${extra}` : head;
}

function destinationLabel(count) {
  return count === 1 ? '1 destination' : `${count} destinations`;
}

const Pill = styled.span`
  font-size: 0.78rem;
  color: ${({ $highlight }) => ($highlight ? 'var(--white)' : 'var(--green-800)')};
  background: ${({ $highlight }) => ($highlight ? 'var(--green-800)' : 'var(--green-a08)')};
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
`;

const Chev = styled(ChevronRight)`
  color: var(--ink-a45);
  flex: 0 0 auto;
`;

// ─── List row ────────────────────────────────────────────────────────────────

const Row = styled.div`
  display: grid;
  grid-template-columns: 56px 1fr 200px 32px;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 120ms ease;
  position: relative;

  &:hover { background: var(--white-a55); }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }

  ${({ $selected }) => $selected && `
    background: var(--green-a08);
    box-shadow: inset 3px 0 0 var(--green-800);
    &:hover { background: var(--green-a15); }
  `}

  & + & { border-top: 1px solid var(--shadow-a05); }

  @media (max-width: 640px) {
    grid-template-columns: 48px 1fr auto;
    & > :nth-child(4) { display: none; } /* hide chevron on narrow */
    & > :nth-child(3) { font-size: 0.72rem; }
  }
`;

const RowText = styled.div`
  min-width: 0;
`;

const Name = styled.div`
  font-weight: 600;
  color: var(--black);
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.div`
  font-size: 0.78rem;
  color: var(--ink-a60);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PillCell = styled.div`
  justify-self: end;
`;

export function CustomerListRow({ group, isSelected, onSelect }) {
  const destArr = Array.from(group.destinations || []).filter(Boolean);
  const summary = destinationsSummary(group, 4);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect && onSelect();
    }
  };

  return (
    <Row
      $selected={isSelected}
      tabIndex={0}
      role="button"
      aria-pressed={Boolean(isSelected)}
      aria-label={`${group.customer}, ${destinationLabel(destArr.length)}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
    >
      <CustomerLogoUpload
        customerName={group.customer}
        logoUrl={group.logo?.url || null}
        size={44}
        readOnly
      />
      <RowText>
        <Name>{group.customer}</Name>
        {summary && <Sub>{summary}</Sub>}
      </RowText>
      <PillCell>
        <Pill $highlight={isSelected}>{destinationLabel(destArr.length)}</Pill>
      </PillCell>
      <Chev size={16} aria-hidden="true" />
    </Row>
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

const Card = styled.div`
  background: var(--white-a55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--white-a66);
  border-radius: 12px;
  padding: 14px;
  height: 132px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 24px var(--shadow-a08);
  }
  &:focus-visible { outline: 2px solid var(--green-800); outline-offset: 2px; }

  ${({ $selected }) => $selected && `
    border: 1px solid var(--green-a45);
    box-shadow: 0 0 0 3px var(--green-a08), 0 8px 24px var(--shadow-a08);
  `}
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const CardName = styled.div`
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.2;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

const CardSub = styled.div`
  font-size: 0.78rem;
  color: var(--ink-a60);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardBot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardTextWrap = styled.div`
  min-width: 0;
  flex: 1;
`;

export function CustomerCard({ group, isSelected, onSelect }) {
  const destArr = Array.from(group.destinations || []).filter(Boolean);
  const summary = destinationsSummary(group, 3);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect && onSelect();
    }
  };

  return (
    <Card
      $selected={isSelected}
      tabIndex={0}
      role="button"
      aria-pressed={Boolean(isSelected)}
      aria-label={`${group.customer}, ${destinationLabel(destArr.length)}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
    >
      <CardTop>
        <CustomerLogoUpload
          customerName={group.customer}
          logoUrl={group.logo?.url || null}
          size={40}
          readOnly
        />
        <CardTextWrap>
          <CardName>{group.customer}</CardName>
          {summary && <CardSub>{summary}</CardSub>}
        </CardTextWrap>
      </CardTop>
      <CardBot>
        <Pill $highlight={isSelected}>{destinationLabel(destArr.length)}</Pill>
        <Chev size={14} aria-hidden="true" />
      </CardBot>
    </Card>
  );
}
